import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ClientManagementRecord, PeriodData, PeriodByPeriod, PeriodClientMetrics } from '../../../hooks/useClientManagementData';
import { FilterBar, FYQuarter } from './FilterBar';
import { KpiStrip, CurrencyRow } from './KpiStrip';
import { ClientManagementTable, EnrichedClient, SortField, SortDir } from './ClientManagementTable';
import { PageHeader } from '../../layouts/PageHeader';
import { useClientDetail } from '../../../hooks/useClientDetail';
import { ClientDetailScreen } from './ClientDetail/client-detail-screen';
import { listPageFiltersToClientDetailSharedPeriod } from './ClientDetail/client-detail-period-types';
import {
  getMaxIndianFYStartYear,
  mergeYearMonthsForMultipleFYStartYears,
  sameFyStartYearSelection,
} from '../../../utils/indianFY';

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface ClientManagementProps {
  clients: ClientManagementRecord[];
  periodData: PeriodData | null;
  summaryByPeriod: PeriodByPeriod | null;
  loading: boolean;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

/** Build default FY start years: last 2 complete FYs */
const buildDefaultFYYears = (): string[] => {
  const max = getMaxIndianFYStartYear();
  return [String(max), String(max - 1)];
};

/**
 * Aggregate per-client metrics across multiple year-month buckets from
 * clients_summary_by_period.json.  Summable fields are summed; riskLevel
 * is computed from the aggregated paymentRatio.
 */
function aggregateClientMetrics(
  clientId: string,
  yearMonths: string[],
  summaryByPeriod: PeriodByPeriod
): PeriodClientMetrics | null {
  let totalInvoiced = 0;
  let totalInvoicedBaseCurrency = 0;
  let totalOutstanding = 0;
  let totalPaid = 0;
  let originalCurrency = 'USD';
  let found = false;

  for (const ym of yearMonths) {
    const bucket = summaryByPeriod.by_year_month[ym];
    if (!bucket) continue;
    const m = bucket.clients[clientId];
    if (!m) continue;
    found = true;
    totalInvoiced += m.totalInvoiced;
    totalInvoicedBaseCurrency += m.totalInvoicedBaseCurrency;
    totalOutstanding += m.outstanding;
    totalPaid += m.totalPaid;
    originalCurrency = m.originalCurrency; // should be consistent
  }

  if (!found) return null;

  const paymentRatio = totalInvoiced > 0 ? (totalPaid / totalInvoiced) * 100 : 100;
  const riskLevel: 'Low' | 'Medium' | 'High' =
    paymentRatio >= 90 ? 'Low' : paymentRatio >= 70 ? 'Medium' : 'High';

  return {
    totalInvoiced,
    totalInvoicedBaseCurrency,
    originalCurrency,
    outstanding: totalOutstanding,
    totalPaid,
    paymentRatio,
    riskLevel,
  };
}

/**
 * For a given set of year-month keys, return a Set of client ID strings
 * that appear in client_invoice_by_period.json.
 */
function getActiveClientIds(
  yearMonths: string[],
  periodData: PeriodData
): Set<string> {
  const ids = new Set<string>();
  for (const ym of yearMonths) {
    const bucket = periodData.by_year_month[ym];
    if (!bucket) continue;
    for (const id of Object.keys(bucket.clients)) {
      ids.add(id);
    }
  }
  return ids;
}

/** Map USD line items to local currency using the same ratio as invoiced (base/USD). */
function usdAmountToBaseCurrency(
  usdAmount: number,
  totalInvoicedUsd: number,
  totalInvoicedBase: number,
): number {
  if (totalInvoicedUsd <= 0) return 0;
  return (usdAmount / totalInvoicedUsd) * totalInvoicedBase;
}

function baseRecordToEnriched(c: ClientManagementRecord): EnrichedClient {
  const { totalInvoiced, totalInvoicedBaseCurrency, outstanding } = c;
  const outstandingBaseCurrency =
    totalInvoiced > 0
      ? (outstanding / totalInvoiced) * totalInvoicedBaseCurrency
      : 0;
  const depositBaseCurrency = usdAmountToBaseCurrency(
    c.deposit,
    totalInvoiced,
    totalInvoicedBaseCurrency,
  );
  const depositAdjustmentBaseCurrency = usdAmountToBaseCurrency(
    c.depositAdjustment,
    totalInvoiced,
    totalInvoicedBaseCurrency,
  );
  const netDepositBaseCurrency = usdAmountToBaseCurrency(
    c.netDeposit,
    totalInvoiced,
    totalInvoicedBaseCurrency,
  );
  return {
    id: c.id,
    name: c.name,
    filename: c.filename,
    country: c.country,
    originalCurrency: c.originalCurrency,
    totalInvoiced,
    totalInvoicedBaseCurrency,
    outstanding,
    outstandingBaseCurrency,
    deposit: c.deposit,
    depositBaseCurrency,
    depositAdjustment: c.depositAdjustment,
    depositAdjustmentBaseCurrency,
    netDeposit: c.netDeposit,
    netDepositBaseCurrency,
    riskLevel: c.riskLevel,
    paymentRatio: c.paymentRatio,
  };
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

export const ClientManagement: React.FC<ClientManagementProps> = ({
  clients,
  periodData,
  summaryByPeriod,
  loading,
}) => {
  /* ── Client detail state ───────────────────────────────────────── */
  const detail = useClientDetail();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedClient, setSelectedClient] = useState<EnrichedClient | null>(null);
  const detailUrlSyncKey = useRef<string>('');

  /* ── Filter state ──────────────────────────────────────────────── */
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

  // Calendar filter
  const [calendarYears, setCalendarYears] = useState<string[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(''); // "1"-"12" or ""

  // Indian FY filter
  const [fyYears, setFYYears] = useState<string[]>([]);       // populated after data loads
  const [fyQuarter, setFYQuarter] = useState<FYQuarter | ''>('');

  // Sort
  const [sortBy, setSortBy] = useState<SortField>('totalInvoiced');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  /* ── One-time default FY initialisation ────────────────────────── */
  const defaultFYYears = useMemo(() => buildDefaultFYYears(), []);
  const fyInitialised = useRef(false);

  useEffect(() => {
    if (!fyInitialised.current && clients.length > 0) {
      setFYYears(defaultFYYears);
      fyInitialised.current = true;
    }
  }, [clients, defaultFYYears]);

  /* ── Derived: all currencies in the full client list ───────────── */
  const allCurrencies = useMemo(() => {
    const set = new Set<string>();
    for (const c of clients) set.add(c.originalCurrency);
    return Array.from(set).sort();
  }, [clients]);

  /* ── Active filter mode ─────────────────────────────────────────── */
  // calendar takes precedence when set; FY is secondary
  const calendarActive = calendarYears.length > 0;
  const fyActive = fyYears.length > 0 && !calendarActive;

  /* ── Resolve active year-month keys ─────────────────────────────── */
  const activeYearMonths = useMemo((): string[] | null => {
    if (calendarActive) {
      const months: string[] = [];
      for (const year of calendarYears) {
        if (calendarMonth) {
          months.push(`${year}-${String(calendarMonth).padStart(2, '0')}`);
        } else {
          for (let m = 1; m <= 12; m++) {
            months.push(`${year}-${String(m).padStart(2, '0')}`);
          }
        }
      }
      return months;
    }
    if (fyActive && fyYears.length > 0) {
      return mergeYearMonthsForMultipleFYStartYears(fyYears, fyQuarter || 'Full');
    }
    return null; // no period filter
  }, [calendarActive, calendarYears, calendarMonth, fyActive, fyYears, fyQuarter]);

  /* ── Active client ID set (from client_invoice_by_period) ─────── */
  const activeClientIds = useMemo((): Set<string> | null => {
    if (!activeYearMonths || !periodData) return null;
    return getActiveClientIds(activeYearMonths, periodData);
  }, [activeYearMonths, periodData]);

  /* ── Enriched + filtered client list ─────────────────────────────── */
  const enrichedClients = useMemo((): EnrichedClient[] => {
    return clients
      .filter(client => {
        // Period filter
        if (activeClientIds !== null) {
          if (!activeClientIds.has(String(client.id))) return false;
        }
        // Currency filter
        if (selectedCurrencies.length > 0 && !selectedCurrencies.includes(client.originalCurrency)) {
          return false;
        }
        // Search
        if (searchTerm.trim()) {
          const s = searchTerm.toLowerCase();
          if (
            !client.name.toLowerCase().includes(s) &&
            !String(client.id).includes(s)
          ) return false;
        }
        return true;
      })
      .map(client => {
        let metrics: PeriodClientMetrics | null = null;

        // Enrich with period-specific metrics when a period filter is active
        if (activeYearMonths && summaryByPeriod) {
          metrics = aggregateClientMetrics(
            String(client.id),
            activeYearMonths,
            summaryByPeriod
          );
        }

        const totalInvoiced = metrics?.totalInvoiced ?? client.totalInvoiced;
        const totalInvoicedBaseCurrency =
          metrics?.totalInvoicedBaseCurrency ?? client.totalInvoicedBaseCurrency;
        const outstanding = metrics?.outstanding ?? client.outstanding;
        /** Deposit lines are lifetime totals from summary — never period-scoped. */
        const deposit = client.deposit;
        const depositAdjustment = client.depositAdjustment;
        const netDeposit = client.netDeposit;
        const riskLevel = metrics?.riskLevel ?? client.riskLevel;
        const paymentRatio = metrics?.paymentRatio ?? client.paymentRatio;

        // Compute base-currency outstanding proportionally
        const outstandingBaseCurrency =
          totalInvoiced > 0
            ? (outstanding / totalInvoiced) * totalInvoicedBaseCurrency
            : 0;

        // Local deposit: use client's lifetime invoiced mix (same as baseRecordToEnriched)
        const depositBaseCurrency = usdAmountToBaseCurrency(
          deposit,
          client.totalInvoiced,
          client.totalInvoicedBaseCurrency,
        );
        const depositAdjustmentBaseCurrency = usdAmountToBaseCurrency(
          depositAdjustment,
          client.totalInvoiced,
          client.totalInvoicedBaseCurrency,
        );
        const netDepositBaseCurrency = usdAmountToBaseCurrency(
          netDeposit,
          client.totalInvoiced,
          client.totalInvoicedBaseCurrency,
        );

        return {
          id: client.id,
          name: client.name,
          filename: client.filename,
          country: client.country,
          originalCurrency: metrics?.originalCurrency ?? client.originalCurrency,
          totalInvoiced,
          totalInvoicedBaseCurrency,
          outstanding,
          outstandingBaseCurrency,
          deposit,
          depositBaseCurrency,
          depositAdjustment,
          depositAdjustmentBaseCurrency,
          netDeposit,
          netDepositBaseCurrency,
          riskLevel,
          paymentRatio,
        } satisfies EnrichedClient;
      });
  }, [
    clients,
    activeClientIds,
    activeYearMonths,
    summaryByPeriod,
    selectedCurrencies,
    searchTerm,
  ]);

  /* ── Sorted client list ──────────────────────────────────────────── */
  const sortedClients = useMemo((): EnrichedClient[] => {
    return [...enrichedClients].sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (sortBy) {
        case 'name':                       av = a.name; bv = b.name; break;
        case 'totalInvoiced':              av = a.totalInvoiced; bv = b.totalInvoiced; break;
        case 'totalInvoicedBaseCurrency':  av = a.totalInvoicedBaseCurrency; bv = b.totalInvoicedBaseCurrency; break;
        case 'outstanding':                av = a.outstanding; bv = b.outstanding; break;
        case 'outstandingBaseCurrency':    av = a.outstandingBaseCurrency; bv = b.outstandingBaseCurrency; break;
        case 'deposit':                    av = a.deposit; bv = b.deposit; break;
        case 'depositAdjustment':          av = a.depositAdjustment; bv = b.depositAdjustment; break;
        case 'netDeposit':                 av = a.netDeposit; bv = b.netDeposit; break;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        const cmp = av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [enrichedClients, sortBy, sortDir]);

  /* ── KPI aggregation ─────────────────────────────────────────────── */
  const kpis = useMemo(() => {
    let totalInvoicedUSD = 0;
    let totalOutstandingUSD = 0;
    let totalDeposit = 0;
    let totalDepositAdj = 0;
    let totalBalance = 0;
    const ccyMap = new Map<string, { clientCount: number; invoicedBase: number; invoicedUSD: number }>();

    for (const c of enrichedClients) {
      totalInvoicedUSD += c.totalInvoiced;
      totalOutstandingUSD += c.outstanding;
      totalDeposit += c.deposit;
      totalDepositAdj += c.depositAdjustment;
      totalBalance += c.netDeposit;

      const ccy = c.originalCurrency;
      const existing = ccyMap.get(ccy);
      if (existing) {
        existing.clientCount += 1;
        existing.invoicedBase += c.totalInvoicedBaseCurrency;
        existing.invoicedUSD += c.totalInvoiced;
      } else {
        ccyMap.set(ccy, {
          clientCount: 1,
          invoicedBase: c.totalInvoicedBaseCurrency,
          invoicedUSD: c.totalInvoiced,
        });
      }
    }

    const currencyRows: CurrencyRow[] = Array.from(ccyMap.entries())
      .map(([ccy, v]) => ({ ccy, ...v }))
      .sort((a, b) => b.invoicedUSD - a.invoicedUSD);

    return {
      totalClients: enrichedClients.length,
      totalInvoicedUSD,
      totalOutstandingUSD,
      totalDeposit,
      totalDepositAdj,
      totalBalance,
      currencyRows,
    };
  }, [enrichedClients]);

  /* ── Sort handler ────────────────────────────────────────────────── */
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  /**
   * Deep link: `?clientId=` (canonical). Legacy `?detail=<filename>` still opens
   * the client and is replaced with `clientId` so URLs stay stable when JSON
   * filenames change.
   */
  useEffect(() => {
    if (loading || clients.length === 0) return;
    const detailFn = searchParams.get('detail');
    const clientIdParam = searchParams.get('clientId');
    /* URL has no detail deep link — reset sync key (see handleBack race note below). */
    if (!detailFn && !clientIdParam) {
      detailUrlSyncKey.current = '';
      return;
    }

    const base = detailFn
      ? clients.find(c => c.filename === detailFn)
      : clients.find(c => String(c.id) === String(clientIdParam));
    if (!base?.filename) return;

    const syncKey = String(base.id);

    if (detailUrlSyncKey.current === syncKey) {
      if (detailFn) {
        setSearchParams(
          prev => {
            const n = new URLSearchParams(prev);
            n.set('clientId', syncKey);
            n.delete('detail');
            return n;
          },
          { replace: true },
        );
      }
      return;
    }

    detailUrlSyncKey.current = syncKey;
    setSelectedClient(baseRecordToEnriched(base));
    void detail.loadClient(base.filename);

    if (detailFn) {
      setSearchParams(
        prev => {
          const n = new URLSearchParams(prev);
          n.set('clientId', syncKey);
          n.delete('detail');
          return n;
        },
        { replace: true },
      );
    }
  }, [loading, clients, searchParams, detail.loadClient, setSearchParams]);

  /* ── Detail header period: mirror list FilterBar when drilling in ─── */
  const detailInitialSharedPeriod = useMemo(
    () =>
      listPageFiltersToClientDetailSharedPeriod({
        calendarYears,
        calendarMonth,
        fyYears,
        fyQuarter: fyQuarter || '',
      }),
    [calendarYears, calendarMonth, fyYears, fyQuarter],
  );

  /* ── Has any filter ──────────────────────────────────────────────── */
  const hasAnyFilter =
    Boolean(searchTerm) ||
    selectedCurrencies.length > 0 ||
    calendarYears.length > 0 ||
    !sameFyStartYearSelection(fyYears, defaultFYYears) ||
    Boolean(fyQuarter);

  /* ── Clear all ───────────────────────────────────────────────────── */
  const handleClearAll = () => {
    setSearchTerm('');
    setSelectedCurrencies([]);
    setCalendarYears([]);
    setCalendarMonth('');
    setFYYears(defaultFYYears);
    setFYQuarter('');
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  const filterBar = (
    <FilterBar
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      allCurrencies={allCurrencies}
      selectedCurrencies={selectedCurrencies}
      onCurrencyChange={setSelectedCurrencies}
      calendarYears={calendarYears}
      onCalendarYearsChange={v => { setCalendarYears(v); if (v.length === 0) setCalendarMonth(''); }}
      calendarMonth={calendarMonth}
      onCalendarMonthChange={setCalendarMonth}
      fyYears={fyYears}
      onFYYearsChange={v => { setFYYears(v); }}
      defaultFYYears={defaultFYYears}
      fyQuarter={fyQuarter}
      onFYQuarterChange={setFYQuarter}
      hasAnyFilter={hasAnyFilter}
      onClearAll={handleClearAll}
    />
  );

  /* ── If a client is selected, show detail page ─────────────────── */
  if (selectedClient !== null) {
    const handleBack = () => {
      /*
       * Do not clear detailUrlSyncKey here. If clientId is still in the URL for a
       * frame while selectedClient is already null, the effect would see ref '' and
       * reopen detail. Keeping ref === client id until the URL drops clientId makes
       * the effect hit the early-return branch instead.
       */
      setSelectedClient(null);
      detail.clearClient();
      setSearchParams(prev => {
        const n = new URLSearchParams(prev);
        n.delete('detail');
        n.delete('clientId');
        return n;
      }, { replace: true });
    };

    const detailLoadingHeader = (
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white hover:text-slate-900 transition-colors flex-none shrink-0 bg-white/80"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
          {selectedClient.name}
        </h1>
      </div>
    );

    if (detail.loading) {
      return (
        <>
          <PageHeader>{detailLoadingHeader}</PageHeader>
          <div className="p-6 flex items-center justify-center py-24">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="ml-3 text-slate-500 text-sm">Loading client data…</span>
          </div>
        </>
      );
    }

    if (detail.error) {
      return (
        <>
          <PageHeader>{detailLoadingHeader}</PageHeader>
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 text-sm font-medium">Failed to load client data</p>
              <p className="text-red-500 text-xs mt-1">{detail.error}</p>
              <button
                onClick={handleBack}
                className="mt-4 text-[12px] font-medium text-blue-600 hover:text-blue-800"
              >
                ← Back to list
              </button>
            </div>
          </div>
        </>
      );
    }

    if (detail.data) {
      return (
        <ClientDetailScreen
          data={detail.data}
          onBack={handleBack}
          initialPeriodFromList={detailInitialSharedPeriod}
        />
      );
    }
  }

  return (
    <>
      <PageHeader title="Client Management" filters={filterBar} />

      <div className="p-6 space-y-4">
        {/* KPI strip */}
        <KpiStrip
          totalClients={kpis.totalClients}
          totalInvoicedUSD={kpis.totalInvoicedUSD}
          totalOutstandingUSD={kpis.totalOutstandingUSD}
          totalDeposit={kpis.totalDeposit}
          totalDepositAdj={kpis.totalDepositAdj}
          totalBalance={kpis.totalBalance}
          currencyRows={kpis.currencyRows}
        />

        {/* Main table */}
        <ClientManagementTable
          clients={sortedClients}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onClientClick={(client) => {
            if (client.filename) {
              detailUrlSyncKey.current = String(client.id);
              setSelectedClient(client);
              void detail.loadClient(client.filename);
              setSearchParams(prev => {
                const n = new URLSearchParams(prev);
                n.set('clientId', String(client.id));
                n.delete('detail');
                return n;
              }, { replace: true });
            }
          }}
          loading={loading}
        />
      </div>
    </>
  );
};

export default ClientManagement;
